/**
 * Iter122 — Field Operations → Time (top-level page)
 *
 * Per directive: Time is moved OUT of Cases and INTO the Field Operations
 * module. Each user only sees their own time entries here; admins have a
 * dedicated management view under Admin → Field Reports.
 */
import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from '../hooks/use-toast';
import { BACKEND_URL } from '../config/canonical';
import { useAuth } from '../context/AuthContext';
import { Clock, Play, Square, RefreshCw, Loader2, Plus } from 'lucide-react';

const fmtHMS = (secs) => {
  const s = Math.max(0, Math.floor(secs || 0));
  const h = Math.floor(s / 3600), m = Math.floor((s % 3600) / 60), r = s % 60;
  return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}:${String(r).padStart(2, '0')}`;
};
const dt = (v) => v ? new Date(v).toLocaleString() : '—';

export default function FieldOperationsTime() {
  const navigate = useNavigate();
  const { isAdmin } = useAuth();
  const token = localStorage.getItem('nexus_token');
  const H = { Authorization: `Bearer ${token}` };
  const JH = { ...H, 'Content-Type': 'application/json' };

  const [cases, setCases] = useState([]);
  const [selectedCase, setSelectedCase] = useState('');
  const [active, setActive] = useState(null);
  const [today, setToday] = useState({ entries: [], total_seconds_today: 0 });
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [cr, ar, tr, hr] = await Promise.all([
        fetch(`${BACKEND_URL}/api/cases`, { headers: H }),
        fetch(`${BACKEND_URL}/api/time-entries/me/active`, { headers: H }),
        fetch(`${BACKEND_URL}/api/time-entries/me/today`, { headers: H }),
        fetch(`${BACKEND_URL}/api/time-entries/me/history?days=30`, { headers: H }),
      ]);
      setCases(cr.ok ? await cr.json() : []);
      setActive(ar.ok ? await ar.json() : null);
      setToday(tr.ok ? await tr.json() : { entries: [], total_seconds_today: 0 });
      setHistory(hr.ok ? await hr.json() : []);
    } finally { setLoading(false); }
    // eslint-disable-next-line
  }, [token]);

  useEffect(() => { load(); }, [load]);

  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    if (!active || active.status !== 'ACTIVE') return;
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, [active]);
  const liveSeconds = active
    ? Math.max(0, Math.floor((now - new Date(active.start_time).getTime()) / 1000) - (active.paused_seconds || 0))
    : 0;

  const startOperation = async () => {
    if (!selectedCase) return toast({ title: 'Select a case', variant: 'destructive' });
    setBusy(true);
    try {
      const r = await fetch(`${BACKEND_URL}/api/time-entries/start`, {
        method: 'POST', headers: JH,
        body: JSON.stringify({ case_id: selectedCase, activity: 'SURVEILLANCE' }),
      });
      const d = await r.json();
      if (!r.ok) return toast({ title: 'Could not start', description: d.detail || 'Error', variant: 'destructive' });
      toast({ title: 'Operation started' });
      await load();
    } finally { setBusy(false); }
  };

  const endOperation = async () => {
    if (!active) return;
    setBusy(true);
    try {
      const r = await fetch(`${BACKEND_URL}/api/time-entries/${active.id}/end`, {
        method: 'POST', headers: JH, body: JSON.stringify({}),
      });
      const d = await r.json();
      if (!r.ok) toast({ title: 'Failed', description: d.detail || 'Error', variant: 'destructive' });
      else toast({ title: 'Ended' });
      await load();
    } finally { setBusy(false); }
  };

  return (
    <div className="space-y-6" data-testid="field-ops-time-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 rounded-lg bg-emerald-600/20 border border-emerald-500/30 flex items-center justify-center">
            <Clock className="w-6 h-6 text-emerald-400" />
          </div>
          <div>
            <div className="text-xs text-emerald-400 uppercase tracking-wider font-mono">Field Operations · Time</div>
            <h1 className="text-2xl font-bold text-white">My Operation Time</h1>
            <p className="text-slate-400 text-sm">Your private time entries — visible only to you and admin.</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {isAdmin && (
            <Button
              onClick={() => navigate('/field-operations/new')}
              className="bg-amber-500 hover:bg-amber-400 !text-slate-900 font-semibold"
              data-testid="create-operation-assignment-btn"
            >
              <Plus className="w-4 h-4 mr-1" /> Create New Operation Assignment
            </Button>
          )}
          <Button variant="ghost" onClick={load} className="text-slate-300">
            <RefreshCw className={`w-4 h-4 mr-1 ${loading ? 'animate-spin' : ''}`} /> Refresh
          </Button>
        </div>
      </div>

      {/* Session control */}
      <Card className="bg-[#131A2A] border-[#334155]">
        <CardHeader className="pb-3">
          <CardTitle className="text-white text-base">Current Operation</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          {active ? (
            <div className="flex items-center justify-between flex-wrap gap-3">
              <div>
                <div className="text-3xl font-mono font-bold text-emerald-300" data-testid="active-time-timer">
                  {fmtHMS(liveSeconds)}
                </div>
                <div className="text-xs text-slate-400 mt-1">
                  Started {dt(active.start_time)} · Case {active.case_number || active.case_id}
                  <Badge className="ml-2 text-[10px] bg-emerald-500/20 text-emerald-300">{active.status}</Badge>
                </div>
              </div>
              <Button className="bg-red-600 hover:bg-red-500 text-white" onClick={endOperation} disabled={busy} data-testid="end-operation-btn">
                <Square className="w-4 h-4 mr-1" /> End Operation
              </Button>
            </div>
          ) : (
            <div className="flex flex-wrap items-end gap-3">
              <div className="flex-1 min-w-[260px]">
                <label className="text-xs text-slate-400">Case</label>
                <Select value={selectedCase} onValueChange={setSelectedCase}>
                  <SelectTrigger className="bg-slate-800 border-slate-600 text-white" data-testid="time-case-select"><SelectValue placeholder="Select case" /></SelectTrigger>
                  <SelectContent>
                    {cases.map(c => <SelectItem key={c.id} value={c.id}>{c.case_number} — {c.title}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <Button className="bg-emerald-600 hover:bg-emerald-500 text-white" onClick={startOperation} disabled={busy || !selectedCase} data-testid="start-operation-btn">
                {busy ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Play className="w-4 h-4 mr-1" />} Start Operation
              </Button>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Today totals */}
      <Card className="bg-emerald-950/30 border-emerald-700/40">
        <CardContent className="py-4">
          <div className="text-xs text-emerald-300 uppercase mb-1">Total Time Today</div>
          <div className="text-3xl font-mono font-bold text-emerald-200">{fmtHMS(today.total_seconds_today)}</div>
          <div className="text-xs text-emerald-300/70 mt-1">{today.entries?.length || 0} entries today</div>
        </CardContent>
      </Card>

      {/* History */}
      <Card className="bg-[#131A2A] border-[#334155]">
        <CardHeader className="pb-3">
          <CardTitle className="text-white text-base">Time Entry History (last 30 days)</CardTitle>
        </CardHeader>
        <CardContent>
          {history.length === 0 ? (
            <div className="text-sm text-slate-500 text-center py-6">No time entries yet.</div>
          ) : (
            <div className="divide-y divide-slate-800">
              {history.map(e => (
                <div key={e.id} className="py-2 flex items-center justify-between flex-wrap gap-2" data-testid={`history-entry-${e.id}`}>
                  <div>
                    <div className="text-sm text-white">
                      {e.activity?.replace(/_/g, ' ')}
                      <Badge className="ml-2 text-[10px] bg-slate-700 text-slate-200">{e.status}</Badge>
                      {e.approval_status && <Badge className={`ml-1 text-[10px] ${e.approval_status === 'APPROVED' ? 'bg-blue-500/20 text-blue-300' : 'bg-red-500/20 text-red-300'}`}>{e.approval_status}</Badge>}
                    </div>
                    <div className="text-xs text-slate-400">Case {e.case_number || e.case_id} · Start {dt(e.start_time)} · End {dt(e.end_time)} · {fmtHMS(e.duration_seconds)}</div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
